type Value = null | undefined | string | number | boolean | object | Array<unknown>;

/**
 * Checks if a value is empty. A value is considered empty unless it's an
 * object with own enumerable properties, a non-empty array, or a non-empty string.
 *
 * @param value - The value to check
 * @returns `true` if the value is empty, `false` otherwise
 *
 * @example
 * isEmpty(null) // => true
 * isEmpty(undefined) // => true
 * isEmpty('') // => true
 * isEmpty([]) // => true
 * isEmpty({}) // => true
 * isEmpty('abc') // => false
 * isEmpty([1, 2, 3]) // => false
 * isEmpty({ a: 1 }) // => false
 */
export const isEmpty = (value: Value): boolean => {
  // Handle null and undefined
  if (value == null) {
    return true;
  }

  // Handle strings
  if (typeof value === 'string') {
    return value.length === 0;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  // Handle objects (plain objects only, arrays are already handled above)
  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  // For all other types, consider them not empty
  return false;
};
